/**
 * The `ComplianceOverview.layers` merge policy (SE-8547).
 *
 * `ComplianceLayerGroup` and `ComplianceRuleEvaluation` have no cache
 * identity. Without a policy, Apollo replaces the `layers` array wholesale on
 * every write. That forced every read of it to select a SUPERSET of every
 * sibling's evaluation fields (the rule stated on `ComplianceLayers` in
 * `operations.graphql`). Once SE-8522 had to drop `trend.points` from
 * `ComplianceLayers` to stay under the server's complexity ceiling, no read was
 * a superset of the others any more. With `ComplianceLayers`,
 * `CompliancePlanStatus` and `ClubObjectives` all mounted on the Compliance
 * tab, each answer left the other two reads incomplete, each of those
 * refetched, and the cycle never settled (hundreds of requests per operation
 * per minute on the dev nightly).
 *
 * This merges the array the way Apollo already merges a normalized entity. A
 * group is matched by `layer` and an evaluation by `ruleId`. Fields the
 * incoming answer carries win, and fields only the existing copy carries
 * survive. The scope is safe because the parent is keyed on
 * `organizationId` + `scenarioId` (SE-7675), so a `ruleId` that repeats across
 * scenarios is never matched against another scenario's copy. The reason
 * `keyFields` on the evaluation itself was rejected no longer applies.
 *
 * The INCOMING list stays authoritative for membership. A group or rule the
 * latest answer omits is dropped, never kept from an older write.
 * @module providers/complianceLayersMerge
 */

import { isReference } from "@apollo/client/utilities";

import type { FieldPolicy } from "@apollo/client/cache";

/** One stored, unnormalized object, as a merge function receives it. */
type StoreObject = Readonly<Record<string, unknown>>;

/**
 * Whether a stored value is an inline (unnormalized) object that can be
 * merged field by field, not a scalar, an array or a normalized reference.
 * @param value - The stored value.
 * @returns True for an inline object.
 */
const isInlineObject = (value: unknown): value is StoreObject =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  !isReference(value);

/**
 * Whether two inline objects are copies of different types. Only when BOTH
 * name a type and the names differ, which is Apollo's own `mergeObjects` rule:
 * a copy written without `__typename` (a hand-built payload, or a client that
 * does not add it) is not evidence of a different type, and treating it as one
 * dropped `trend.points` on the next narrower write.
 * @param existing - The copy already in the cache.
 * @param incoming - The copy being written.
 * @returns True when the two are known to be different types.
 */
const typesDiffer = (existing: StoreObject, incoming: StoreObject): boolean =>
  existing.__typename !== undefined &&
  incoming.__typename !== undefined &&
  existing.__typename !== incoming.__typename;

/**
 * Deep-merges two copies of one inline object. Incoming fields win; a nested
 * inline object present in both (such as `trend`) is merged the same way, so
 * a narrower selection of it does not strip the wider one. Arrays are taken
 * from incoming as-is, because nothing identifies their elements.
 * @param existing - The copy already in the cache.
 * @param incoming - The copy being written.
 * @returns The merged object.
 */
const mergeInline = (
  existing: StoreObject,
  incoming: StoreObject
): StoreObject =>
  typesDiffer(existing, incoming)
    ? incoming
    : Object.fromEntries(
        Object.entries({ ...existing, ...incoming }).map(([key, value]) => {
          const previous = existing[key];
          return [
            key,
            key in incoming && isInlineObject(previous) && isInlineObject(value)
              ? mergeInline(previous, value)
              : value,
          ];
        })
      );

/**
 * Merges an incoming list against an existing one, matching elements by a key
 * field. Membership and order come from incoming.
 * @param existing - The list already in the cache.
 * @param incoming - The list being written.
 * @param keyField - The field that identifies an element.
 * @param mergeElement - How to merge two copies of one element.
 * @returns The merged list.
 */
const mergeKeyedList = (
  existing: readonly unknown[] | undefined,
  incoming: readonly unknown[],
  keyField: string,
  mergeElement: (previous: StoreObject, next: StoreObject) => StoreObject
): readonly unknown[] => {
  const previousByKey = new Map(
    (existing ?? [])
      .filter(isInlineObject)
      .map(element => [element[keyField], element] as const)
  );
  return incoming.map(element => {
    if (!isInlineObject(element)) return element;
    const previous = previousByKey.get(element[keyField]);
    return previous === undefined ? element : mergeElement(previous, element);
  });
};

/**
 * Merges two copies of one layer group: evaluations matched by `ruleId`.
 * @param existing - The group already in the cache.
 * @param incoming - The group being written.
 * @returns The merged group.
 */
const mergeLayerGroup = (
  existing: StoreObject,
  incoming: StoreObject
): StoreObject => {
  const merged = mergeInline(existing, incoming);
  return Array.isArray(incoming.evaluations)
    ? {
        ...merged,
        evaluations: mergeKeyedList(
          Array.isArray(existing.evaluations) ? existing.evaluations : [],
          incoming.evaluations,
          "ruleId",
          mergeInline
        ),
      }
    : merged;
};

/** The field policy for `ComplianceOverview.layers`. */
export const COMPLIANCE_LAYERS_FIELD_POLICY: FieldPolicy<readonly unknown[]> = {
  merge: (existing, incoming) =>
    mergeKeyedList(existing, incoming, "layer", mergeLayerGroup),
};
